import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { 
  QrCode, Users, ArrowLeft, LogOut, Lock, Unlock, 
  UserX, UserCheck, BarChart3, Car, Baby, Heart, Wrench,
  Plus, Loader2, Upload, FileText, CheckCircle, XCircle, AlertCircle, RefreshCw, Trash2, Unlink
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getIdToken } from "../lib/firebase";

const API = process.env.REACT_APP_BACKEND_URL;

const purposeIcons = {
  vehicle: Car,
  child: Baby,
  elderly: Heart,
  machine: Wrench,
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, userProfile } = useAuth();
  const [stats, setStats] = useState(null);
  const [qrCodes, setQRCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [newQRCode, setNewQRCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [qrToUnassign, setQrToUnassign] = useState(null);
  const [unassigning, setUnassigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = await getIdToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      const [statsRes, qrsRes, usersRes] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers }),
        fetch(`${API}/api/admin/qrs`, { headers }),
        fetch(`${API}/api/admin/users`, { headers }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (qrsRes.ok) setQRCodes(await qrsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQR = async () => {
    setCreating(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/admin/qr/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ code: newQRCode.trim() || null }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to create QR code");
      }

      toast.success(`QR code "${data.code}" created!`);
      setNewQRCode("");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleBulkCreate = async () => {
    if (bulkCount < 1 || bulkCount > 100) {
      toast.error("Count must be between 1 and 100");
      return;
    }

    setBulkCreating(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/admin/qr/bulk-create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ count: bulkCount }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to create QR codes");
      }

      toast.success(`Created ${data.codes.length} QR codes!`);
      setBulkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkCreating(false);
    }
  };

  const handleBlockQR = async (code, isBlocked) => {
    const action = isBlocked ? "unblock" : "block";
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/admin/qr/${code}/${action}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to update QR");

      toast.success(`QR code ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetOwner = async (code) => {
    if (!confirm(`Reset ownership for QR ${code}? This will make it unassigned.`)) return;
    
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/admin/qr/${code}/reset-owner`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to reset owner");

      toast.success("QR ownership reset");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Open unassign confirmation dialog
  const openUnassignDialog = (qr) => {
    setQrToUnassign(qr);
    setUnassignDialogOpen(true);
  };

  // Confirm unassign QR
  const confirmUnassignQR = async () => {
    if (!qrToUnassign) return;
    
    setUnassigning(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/admin/qr/${qrToUnassign.code}/reset-owner`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to unassign QR");

      toast.success(`QR code ${qrToUnassign.code} has been unassigned`);
      setUnassignDialogOpen(false);
      setQrToUnassign(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUnassigning(false);
    }
  };

  // Open delete user confirmation dialog
  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Confirm delete user
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/admin/user/${userToDelete.firebase_uid}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to delete user");

      toast.success(data.message);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleUserActive = async (firebase_uid) => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/admin/user/${firebase_uid}/toggle-active`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to update user");

      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2563EB] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white px-6 py-4 border-b border-[#E2E8F0] shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-[#475569] hover:bg-[#F1F5F9]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
                Admin Panel
              </h1>
              <p className="text-sm text-[#475569]">{userProfile?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-[#475569] hover:bg-[#F1F5F9]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="qrcodes" data-testid="tab-qrcodes">
              <QrCode className="w-4 h-4 mr-2" />
              QR Codes
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-[#2563EB]">{stats?.total_qrs || 0}</p>
                  <p className="text-sm text-gray-600">Total QRs</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-[#16A34A]">{stats?.assigned_qrs || 0}</p>
                  <p className="text-sm text-gray-600">Assigned</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-[#F59E0B]">{stats?.unassigned_qrs || 0}</p>
                  <p className="text-sm text-gray-600">Unassigned</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-[#DC2626]">{stats?.blocked_qrs || 0}</p>
                  <p className="text-sm text-gray-600">Blocked</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm col-span-2">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-[#8B5CF6]">{stats?.total_users || 0}</p>
                  <p className="text-sm text-gray-600">Total Users</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* QR Codes Tab */}
          <TabsContent value="qrcodes">
            <div className="flex gap-2 mb-4">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <Button onClick={() => setDialogOpen(true)} className="bg-[#2563EB]" data-testid="create-qr-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create QR
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create QR Code</DialogTitle>
                    <DialogDescription>
                      Enter a custom 12-character code or leave empty for auto-generation.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>QR Code (optional)</Label>
                      <Input
                        placeholder="e.g., ABC123DEF456 (12 chars)"
                        value={newQRCode}
                        onChange={(e) => setNewQRCode(e.target.value.toUpperCase())}
                        maxLength={12}
                        className="font-mono"
                        data-testid="new-qr-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleCreateQR} disabled={creating} data-testid="confirm-create-btn">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
                <Button variant="outline" onClick={() => setBulkDialogOpen(true)} data-testid="bulk-create-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Create
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Bulk Create QR Codes</DialogTitle>
                    <DialogDescription>
                      Auto-generate multiple 12-character QR codes at once.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Number of QR Codes</Label>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        value={bulkCount}
                        onChange={(e) => setBulkCount(parseInt(e.target.value) || 1)}
                        data-testid="bulk-count-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max 100 at a time</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleBulkCreate} disabled={bulkCreating} data-testid="confirm-bulk-btn">
                      {bulkCreating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create {bulkCount} QR Codes
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Button variant="ghost" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-2">
              {qrCodes.map((qr) => {
                const Icon = purposeIcons[qr.purpose] || QrCode;
                return (
                  <Card key={qr.qr_id} className="bg-white shadow-sm">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-mono font-semibold">{qr.code}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={
                              qr.status === "assigned" ? "default" :
                              qr.status === "blocked" ? "destructive" : "secondary"
                            }>
                              {qr.status}
                            </Badge>
                            {qr.purpose && (
                              <Badge variant="outline" className="text-xs">
                                {qr.purpose}
                              </Badge>
                            )}
                            {qr.owner_id && (
                              <span className="text-xs text-gray-500">
                                Owner: {qr.owner_id.slice(0, 8)}...
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {qr.status === "assigned" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openUnassignDialog(qr)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            data-testid={`unassign-${qr.code}`}
                          >
                            <Unlink className="w-4 h-4 mr-1" />
                            Unassign
                          </Button>
                        )}
                        <Button
                          variant={qr.status === "blocked" ? "default" : "destructive"}
                          size="sm"
                          onClick={() => handleBlockQR(qr.code, qr.status === "blocked")}
                          data-testid={`block-${qr.code}`}
                        >
                          {qr.status === "blocked" ? (
                            <>
                              <Unlock className="w-4 h-4 mr-1" />
                              Unblock
                            </>
                          ) : (
                            <>
                              <Lock className="w-4 h-4 mr-1" />
                              Block
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {qrCodes.length === 0 && (
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-8 text-center text-gray-500">
                    No QR codes yet. Create your first one!
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">{users.length} user(s) registered</p>
              <Button variant="ghost" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
            <div className="space-y-2">
              {users.map((user) => (
                <Card key={user.firebase_uid} className="bg-white shadow-sm">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium">{user.name || user.email}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                            {user.role}
                          </Badge>
                          <Badge variant={user.is_active ? "outline" : "destructive"}>
                            {user.is_active ? "Active" : "Blocked"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    {user.role !== "admin" && (
                      <div className="flex gap-2">
                        <Button
                          variant={user.is_active ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleToggleUserActive(user.firebase_uid)}
                          data-testid={`toggle-user-${user.firebase_uid}`}
                        >
                          {user.is_active ? (
                            <>
                              <UserX className="w-4 h-4 mr-1" />
                              Block
                            </>
                          ) : (
                            <>
                              <UserCheck className="w-4 h-4 mr-1" />
                              Unblock
                            </>
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => openDeleteDialog(user)}
                          data-testid={`delete-user-${user.firebase_uid}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {users.length === 0 && (
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-8 text-center text-gray-500">
                    No users registered yet.
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {userToDelete && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium">{userToDelete.name || userToDelete.email}</p>
              <p className="text-sm text-gray-500">{userToDelete.email}</p>
              <p className="text-xs text-orange-600 mt-2">
                All QR codes owned by this user will be unassigned.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteUser} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign QR Confirmation Dialog */}
      <Dialog open={unassignDialogOpen} onOpenChange={setUnassignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-orange-600 flex items-center gap-2">
              <Unlink className="w-5 h-5" />
              Unassign QR Code
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to unassign this QR code? The owner will lose access to it.
            </DialogDescription>
          </DialogHeader>
          {qrToUnassign && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-mono font-bold text-lg">{qrToUnassign.code}</p>
              {qrToUnassign.purpose && (
                <p className="text-sm text-gray-600">Purpose: {qrToUnassign.purpose}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Owner ID: {qrToUnassign.owner_id?.slice(0, 12)}...
              </p>
              <p className="text-xs text-orange-600 mt-2">
                All data associated with this QR will be cleared. The QR can then be claimed by another user.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnassignDialogOpen(false)} disabled={unassigning}>
              Cancel
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700" 
              onClick={confirmUnassignQR} 
              disabled={unassigning}
            >
              {unassigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlink className="w-4 h-4 mr-2" />}
              Unassign QR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
